import { useAtom, useAtomValue } from 'jotai';

import { useGetCurrentIntentionQuery } from '../../hooks/intentions/useGetCurrentIntentionQuery';
import { useMomentumPilotPatientAccessQuery } from '../../hooks/intentions/useMomentumPilotPatientAccessQuery';
import { sessionInfoAtom } from '../../state';
import { nameToInitials } from '../../utils';
import { inSessionPatientInformationAtom, providerShortIdAtom, selectedClientUserShortIdAtom } from '../state';
import { useGetPatientByShortId } from '../useGetPatientByShortId';
import { INTENTION_V1_KEY } from './constants';
import { type IntentionUse, useIntentionUse } from './useIntentionUse';

/**
 * Hook to determine the current intention setting state for a patient
 * Combines data from multiple atoms and calculates the appropriate UI state
 *
 * @returns Object containing intention state and patient data, or undefined if no patient
 */
export function useIntentionState():
  | {
      appVersion: string;
      use: IntentionUse;
      patientShortId: string;
      preferredName: string;
      initials: string;
      clientUserShortId: string;
      providerShortId: string;
      appointmentShortId: string;
      providerImage: string;
      isLoading: boolean;
    }
  | undefined {
  const [inSessionPatientInfo, setInSessionPatientInfo] = useAtom(inSessionPatientInformationAtom);
  const providerShortId = useAtomValue(providerShortIdAtom) ?? '';
  const clientUserShortId = useAtomValue(selectedClientUserShortIdAtom) ?? '';
  const sessionInfo = useAtomValue(sessionInfoAtom);

  const patientShortId = inSessionPatientInfo?.patientShortId ?? '';
  const appVersion = inSessionPatientInfo?.appVersion ?? '';

  const { loading: patientDataLoading } = useGetPatientByShortId(patientShortId);

  const { pilotAccess, loading: pilotAccessLoading } = useMomentumPilotPatientAccessQuery(
    patientShortId,
    INTENTION_V1_KEY,
    appVersion,
  );

  const intentionCompletedAt = inSessionPatientInfo?.intentionCompletedAt;
  const intentionPromptSentAt = inSessionPatientInfo?.intentionPromptSentAt;
  const appointmentShortId = inSessionPatientInfo?.inSessionAppointmentShortId ?? '';

  const fullName = inSessionPatientInfo?.name ?? '';
  const preferredName = fullName.split(' ')[0] || '';
  const initials = nameToInitials(fullName);

  // Check if there's already an intention for this appointment
  // This can happen if the session was disconnected
  const { loading: intentionLoading } = useGetCurrentIntentionQuery(
    clientUserShortId,
    providerShortId,
    appointmentShortId,
    {
      skip: !appointmentShortId,
      onCompleted: (data) => {
        const intentionCreatedAt = data?.intentionHistory?.currentSessionIntention?.createdAt;
        if (intentionCreatedAt && !intentionCompletedAt) {
          setInSessionPatientInfo((prev) => {
            if (!prev || prev.patientShortId !== patientShortId) return prev;

            return {
              ...prev,
              intentionCompletedAt: intentionCreatedAt,
            };
          });
        }
      },
    },
  );

  const use = useIntentionUse(providerShortId, patientShortId, intentionCompletedAt, intentionPromptSentAt);

  if (!patientShortId || !inSessionPatientInfo || !pilotAccess?.enabled) {
    return undefined;
  }

  return {
    appVersion,
    use,
    patientShortId,
    preferredName,
    initials,
    clientUserShortId,
    providerShortId,
    appointmentShortId,
    providerImage: sessionInfo?.provider.image ?? '',
    isLoading: pilotAccessLoading || intentionLoading || patientDataLoading,
  };
}
